/** A single mark on the canvas. Everything the child draws is a list of these. */

import type { Vec } from './vec.ts';
import { bounds, decimate, smoothPath } from './geom.ts';

export type BrushId = 'crayon' | 'marker' | 'pencil' | 'rainbow';

export interface Stroke {
  brush: BrushId;
  /** CSS colour string, or 'rainbow' sentinel handled by the renderer. */
  color: string;
  width: number;
  points: Vec[];
  /** Per-point speed 0..1, drives width jitter for a hand-drawn feel. */
  speeds: number[];
  /** Random per-stroke seed so crayon texture is stable across frames. */
  seed: number;
  /** Set on ready-made toy shapes: fill the closed path before stroking it. */
  fill?: string;
}

export interface BrushSpec {
  id: BrushId;
  label: string;
  /** Multiplier on the chosen size. */
  scale: number;
  /** How much speed thins the line, 0..1. */
  taper: number;
}

export const BRUSHES: Record<BrushId, BrushSpec> = {
  crayon: { id: 'crayon', label: 'Crayon', scale: 1, taper: 0.35 },
  marker: { id: 'marker', label: 'Marker', scale: 1.35, taper: 0.1 },
  pencil: { id: 'pencil', label: 'Pencil', scale: 0.45, taper: 0.55 },
  rainbow: { id: 'rainbow', label: 'Rainbow', scale: 1.25, taper: 0.2 },
};

export const BRUSH_ORDER: BrushId[] = ['crayon', 'marker', 'pencil', 'rainbow'];

export function createStroke(brush: BrushId, color: string, width: number, seed: number): Stroke {
  return { brush, color, width, points: [], speeds: [], seed };
}

/**
 * Append a point, recording how fast the hand was moving. `scale` is the zoom
 * the point arrived at, so that on a pad you can zoom into: taper is a property
 * of how fast the hand moved across the glass, not of how far the mark
 * travelled in world units.
 */
export function extendStroke(stroke: Stroke, p: Vec, scale = 1): void {
  const prev = stroke.points[stroke.points.length - 1];
  const speed = prev ? Math.min(1, (Math.hypot(p.x - prev.x, p.y - prev.y) * scale) / 26) : 0;
  stroke.points.push(p);
  stroke.speeds.push(speed);
}

/** Tidy a finished stroke: drop oversampled points, then round the corners. */
export function finishStroke(stroke: Stroke): Stroke {
  const kept = decimate(stroke.points, 1.6);
  const keptSpeeds: number[] = [];
  let si = 0;
  for (const p of kept) {
    while (si < stroke.points.length - 1 && stroke.points[si] !== p) si++;
    keptSpeeds.push(stroke.speeds[si] ?? 0);
  }
  const smoothed = smoothPath(kept, 1);
  const speeds: number[] = [];
  for (let i = 0; i < smoothed.length; i++) {
    speeds.push(keptSpeeds[Math.min(keptSpeeds.length - 1, Math.floor((i / smoothed.length) * keptSpeeds.length))] ?? 0);
  }
  return { ...stroke, points: smoothed, speeds };
}

/** Every point of every stroke — the cloud a hull is built from. */
export function strokePoints(strokes: Stroke[]): Vec[] {
  const out: Vec[] = [];
  for (const s of strokes) out.push(...s.points);
  return out;
}

export function strokeBounds(strokes: Stroke[]) {
  return bounds(strokePoints(strokes));
}

/** A dot is a stroke that never moved — pad it so it still makes a shape. */
export function isDot(stroke: Stroke): boolean {
  if (stroke.points.length < 2) return true;
  const b = bounds(stroke.points);
  return b.maxX - b.minX < 4 && b.maxY - b.minY < 4;
}
